import React, { useState, useRef } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  useTheme,
  Popover,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const ALL_MODES = [
  { value: 'law', emoji: '📜', labelKey: 'mode_reg' },
  { value: 'regulation', emoji: '📋', labelKey: 'mode_regulation' },
  { value: 'case_law', emoji: '⚖️', labelKey: 'mode_jp' },
];

const ModeDropdown = ({
  value,
  onChange,
  size = "medium",
  minWidth = { xs: 200, md: 280 },
  showLabel = true,
  label = null,
  disabled = false,
  sx = {},
  listMode = false,
  onSelect = null,
  displayMode = 'dropdown', // 'dropdown' or 'icon'
  multiSelect = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const countryPref = useSelector(state => state.user?.countryPreference);
  const onlyBF = (() => {
    const arr = Array.isArray(countryPref) ? countryPref : countryPref ? [countryPref] : ['MA'];
    return arr.length === 1 && arr[0] === 'BF';
  })();
  const [anchorEl, setAnchorEl] = useState(null);
  const iconButtonRef = useRef(null);

  // Get available modes based on country restrictions
  const hasMA = (() => {
    const arr = Array.isArray(countryPref) ? countryPref : countryPref ? [countryPref] : ['MA'];
    return arr.includes('MA');
  })();

  const getAvailableModes = () => {
    if (onlyBF) return ALL_MODES.filter(m => m.value === 'law');
    // Regulation only available for Morocco
    if (!hasMA) return ALL_MODES.filter(m => m.value !== 'regulation');
    return ALL_MODES;
  };

  const handleModeChange = (event) => {
    const newValue = event.target.value;
    if (onChange && newValue) {
      onChange(event, newValue);
    }
  };

  // ── Multi-select icon mode ─────────────────────────────────────────────
  if (multiSelect && displayMode === 'icon') {
    // Parse comma-separated mode string to array
    const selectedModes = value ? value.split(',').map(m => m.trim()) : ['law'];
    const modes = getAvailableModes();

    const handleToggleMode = (modeValue) => {
      let newSelected;
      if (selectedModes.includes(modeValue)) {
        if (selectedModes.length === 1) return; // prevent empty
        newSelected = selectedModes.filter(m => m !== modeValue);
      } else {
        newSelected = [...selectedModes, modeValue];
      }
      const newValue = newSelected.join(',');
      if (onChange) onChange({ target: { value: newValue } }, newValue);
    };

    const getDisplayLabel = () => {
      const selected = modes.filter(m => selectedModes.includes(m.value));
      if (selected.length === 0) return '📜';
      if (selected.length === 1) {
        return <>{selected[0].emoji} {t(selected[0].labelKey)}</>;
      }
      return selected.map((s, i) => (
        <span key={s.value}>{s.emoji} {t(s.labelKey)}{i < selected.length - 1 ? '  |  ' : ''}</span>
      ));
    };

    return (
      <>
        <Box
          ref={iconButtonRef}
          onClick={() => !disabled && setAnchorEl(iconButtonRef.current)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: theme.palette.action.hover,
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.5 : 1,
            '&:hover': {
              backgroundColor: disabled ? theme.palette.action.hover : theme.palette.action.selected,
            },
          }}
          title={selectedModes.join(', ')}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.7rem',
              fontWeight: 600,
              maxWidth: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.palette.text.primary,
              lineHeight: 1.4,
            }}
          >
            {getDisplayLabel()}
          </Typography>
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ minWidth: 200 }}>
            {modes.map(modeItem => {
              const isSelected = selectedModes.includes(modeItem.value);
              return (
                <MenuItem
                  key={modeItem.value}
                  onClick={() => handleToggleMode(modeItem.value)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1.5 }}
                >
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    sx={{ p: 0.5 }}
                    disableRipple
                  />
                  <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{modeItem.emoji}</span> {t(modeItem.labelKey)}
                  </Typography>
                </MenuItem>
              );
            })}
          </Box>
        </Popover>
      </>
    );
  }

  // ── List-only mode ─────────────────────────────────────────────────────
  if (listMode) {
    const modes = getAvailableModes();
    return (
      <Box sx={{ width: '100%' }}>
        {modes.map(modeItem => (
          <MenuItem
            key={modeItem.value}
            onClick={() => {
              handleModeChange({ target: { value: modeItem.value } });
              if (onSelect) onSelect(modeItem.value);
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{modeItem.emoji}</span>
              <span>{t(modeItem.labelKey)}</span>
            </Box>
          </MenuItem>
        ))}
      </Box>
    );
  }

  // ── Single-select icon mode ────────────────────────────────────────────
  if (displayMode === 'icon') {
    const modes = getAvailableModes();
    const current = ALL_MODES.find(m => m.value === value) || ALL_MODES[0];

    return (
      <>
        <Box
          ref={iconButtonRef}
          onClick={() => !disabled && setAnchorEl(iconButtonRef.current)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: theme.palette.action.hover,
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.5 : 1,
            '&:hover': {
              backgroundColor: disabled ? theme.palette.action.hover : theme.palette.action.selected,
            },
          }}
          title={t(current.labelKey)}
        >
          <span style={{ fontSize: '16px' }}>{current.emoji}</span>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              maxWidth: '70px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.palette.text.primary,
            }}
          >
            {t(current.labelKey)}
          </Typography>
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ width: '100%' }}>
            {modes.map(modeItem => (
              <MenuItem
                key={modeItem.value}
                onClick={() => {
                  handleModeChange({ target: { value: modeItem.value } });
                  setAnchorEl(null);
                  if (onSelect) onSelect(modeItem.value);
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{modeItem.emoji}</span>
                  <span>{t(modeItem.labelKey)}</span>
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Popover>
      </>
    );
  }

  // ── Default dropdown mode ──────────────────────────────────────────────
  const defaultSx = {
    minWidth,
    width: { xs: '100%', md: 'auto' },
    mt: { xs: 0.5, md: 0 },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: theme.palette.background.paper,
      height: { xs: '36px', md: '48px' },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: { xs: '0.75rem', md: '1rem' },
      '&.Mui-focused': {
        color: 'primary.main',
      },
    },
  };

  const modes = getAvailableModes();

  // Multi-select dropdown mode
  if (multiSelect) {
    const selectedModes = value ? value.split(',').map(m => m.trim()) : ['law'];

    const handleMultiChange = (event) => {
      let newSelected = event.target.value; // MUI Select multiple gives array
      if (newSelected.length === 0) return; // prevent empty
      const newValue = newSelected.join(',');
      if (onChange) onChange({ target: { value: newValue } }, newValue);
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControl
          size={size}
          variant="outlined"
          disabled={disabled}
          sx={{ ...defaultSx, ...sx }}
        >
          {showLabel && (
            <InputLabel
              id="mode-select-label"
              sx={{ fontSize: { xs: '0.75rem', md: '1.1rem' } }}
            >
              {label || t("mode")}
            </InputLabel>
          )}
          <Select
            labelId="mode-select-label"
            multiple
            value={selectedModes}
            label={showLabel ? (label || t("mode")) : undefined}
            onChange={handleMultiChange}
            disabled={disabled}
            renderValue={(selected) => {
              return selected
                .map(v => ALL_MODES.find(m => m.value === v))
                .filter(Boolean)
                .map(m => `${m.emoji} ${t(m.labelKey)}`)
                .join(', ');
            }}
            sx={{
              fontSize: { xs: '0.875rem', md: '1.1rem' },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                width: '100%',
              },
            }}
          >
            {modes.map(modeItem => (
              <MenuItem key={modeItem.value} value={modeItem.value}>
                <Checkbox checked={selectedModes.includes(modeItem.value)} size="small" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{modeItem.emoji}</span>
                  <span>{t(modeItem.labelKey)}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  // Single-select dropdown mode
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FormControl
        size={size}
        variant="outlined"
        disabled={disabled}
        sx={{ ...defaultSx, ...sx }}
      >
        {showLabel && (
          <InputLabel
            id="mode-select-label"
            sx={{ fontSize: { xs: '0.75rem', md: '1.1rem' } }}
          >
            {label || t("mode")}
          </InputLabel>
        )}
        <Select
          labelId="mode-select-label"
          value={value}
          label={showLabel ? (label || t("mode")) : undefined}
          onChange={handleModeChange}
          disabled={disabled}
          key={`mode-select-${value}-${onlyBF}`}
          sx={{
            fontSize: { xs: '0.875rem', md: '1.1rem' },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              width: '100%',
            },
          }}
        >
          {modes.map(modeItem => (
            <MenuItem
              key={modeItem.value}
              value={modeItem.value}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                <span>{modeItem.emoji}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t(modeItem.labelKey)}
                </span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ModeDropdown;
